import type { Step as RcStep } from '@sb/remote-control/types';

import {
  type ValidationRule,
  type RoutineView,
  type ValidationContext,
} from '../../types';
import { ActionRequiredError } from '../../util/errors';
import { validateCodeExpression } from '../../util/validators';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

class LocateRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.Locate', 'Locate');
  }

  protected validate({ args, routine, ctx }: CanonicalValidateArgs): void {
    type LocArgs = Extract<RcStep.Arguments, { argumentKind: 'Locate' }>;
    const a = args as LocArgs;
    const isManualMode = (a.planeDetectionMode ?? 'Manual') === 'Manual';

    if (isManualMode) {
      this.checkPlaneSelected(a);
      this.checkPlaneValid(a, routine, ctx);
    }

    this.checkPositionListName(a);
    this.checkCameraEquipment(a, ctx);
    this.checkCaptureConfig(a, ctx);
    this.checkMethod(a, ctx);
  }

  /**
   * Validate camera equipment when `cameraEquipmentId` is set: equipment must
   * exist, have `config.kind === 'Camera'`, and have intrinsics + extrinsics.
   *
   * @throws {ActionRequiredError}
   */
  private checkCameraEquipment(
    args: Extract<RcStep.Arguments, { argumentKind: 'Locate' }>,
    ctx: ValidationContext,
  ): void {
    if (!args.cameraEquipmentId) return;

    const cam = this.findEquipment(ctx, args.cameraEquipmentId);

    if (!cam) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Camera equipment is not set',
        fieldId: 'cameraEquipmentId',
      });
    }

    if (cam.config.kind !== 'Camera') {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Camera equipment must be a camera.',
        fieldId: 'cameraEquipmentId',
      });
    }

    const cfg = cam.config as {
      intrinsicsId?: string | null;
      extrinsicsId?: string | null;
    };

    if (!cfg.intrinsicsId) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Intrinsics on camera equipment must be set.',
        fieldId: 'cameraEquipmentId',
      });
    }

    if (!cfg.extrinsicsId) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Extrinsics on camera equipment must be set.',
        fieldId: 'cameraEquipmentId',
      });
    }
  }

  /**
   * Validate the `method` field. Each `method.kind` has its own checks; this
   * dispatches to per-kind validators.
   *
   * @throws {ActionRequiredError}
   */
  private checkMethod(
    args: Extract<RcStep.Arguments, { argumentKind: 'Locate' }>,
    ctx: ValidationContext,
  ): void {
    const { method } = args;
    if (!method || typeof method !== 'object') return;

    switch (method.kind) {
      case 'BlobDetection2D':
        this.checkMethodBlobDetection2D(method);
        break;
      case 'ShapeDetection2D':
        this.checkMethodShapeDetection2D(method);
        break;
      case 'CustomCheckpointDetection2D':
        this.checkMethodCustomCheckpointDetection2D(method, ctx);
        break;
      case 'TextPromptDetection2D':
        this.checkMethodTextPromptDetection2D(method, ctx);
        break;
      default:
        // Other method kinds have no validation requirements.
        break;
    }
  }

  private checkMethodBlobDetection2D(method: {
    settings: {
      minThreshold: number;
      maxThreshold: number;
      minArea: number;
      maxArea: number;
      filterByCircularity?: boolean;
      minCircularity?: number;
      maxCircularity?: number;
    };
  }): void {
    const s = method.settings;

    if (s.maxThreshold < s.minThreshold) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Max threshold may not be smaller than min threshold',
      });
    }

    if (s.maxArea < s.minArea) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Max area may not be smaller than min area',
      });
    }

    if (
      s.filterByCircularity &&
      s.maxCircularity != null &&
      s.minCircularity != null &&
      s.maxCircularity < s.minCircularity
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Max circularity may not be smaller than min circularity',
      });
    }
  }

  private checkMethodShapeDetection2D(method: {
    settings: { minScale: number; maxScale: number };
    templateImage?: { storageID?: string | null };
  }): void {
    if (method.settings.maxScale < method.settings.minScale) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Max scale may not be smaller than min scale',
      });
    }

    if (!method.templateImage?.storageID) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Template image is not set',
      });
    }
  }

  private checkMethodCustomCheckpointDetection2D(
    method: {
      settings: {
        gpuTaskRunnerId?: string | null;
        modelPath?: string | null;
        modelType?: string | null;
        modelSource?: string | null;
        roboflowId?: string | null;
      };
    },
    ctx: ValidationContext,
  ): void {
    const s = method.settings;

    if (!s.gpuTaskRunnerId) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'No GPU Task Runner selected',
        fieldId: 'method.settings.gpuTaskRunnerId',
      });
    }

    if (!this.findEquipment(ctx, s.gpuTaskRunnerId)) {
      throw new ActionRequiredError({
        kind: 'missingEquipment',
        message: `Unable to find GPU Task Runner with id ${s.gpuTaskRunnerId} in equipment manager.`,
        fieldId: 'method.settings.gpuTaskRunnerId',
      });
    }

    if (s.modelSource === 'roboflow') {
      // A Roboflow-hosted model is identified by `roboflowId` (the model url),
      // not a runner-hosted checkpoint `modelPath`.
      if (!s.roboflowId) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'No Roboflow model selected',
          fieldId: 'method.settings.roboflowId',
        });
      }
    } else if (!s.modelPath) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'No model selected',
        fieldId: 'method.settings.modelPath',
      });
    }

    if (!s.modelType) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'No model type selected',
        fieldId: 'method.settings.modelType',
      });
    }
  }

  private checkMethodTextPromptDetection2D(
    method: {
      settings: {
        textInput?: string | null;
        gpuTaskRunnerId?: string | null;
      };
    },
    ctx: ValidationContext,
  ): void {
    const s = method.settings;

    if (!s.textInput) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Text prompt input is empty',
        fieldId: 'method.settings.textInput',
      });
    }

    if (s.gpuTaskRunnerId && !this.findEquipment(ctx, s.gpuTaskRunnerId)) {
      throw new ActionRequiredError({
        kind: 'missingEquipment',
        message: `Unable to find GPU Task Runner with id ${s.gpuTaskRunnerId} in equipment manager.`,
        fieldId: 'method.settings.gpuTaskRunnerId',
      });
    }
  }

  /**
   * Validate that a plane is selected.
   * Throws ActionRequiredError if not set.
   *
   * @throws {ActionRequiredError}
   */
  private checkPlaneSelected(
    args: Extract<RcStep.Arguments, { argumentKind: 'Locate' }>,
  ): void {
    if (!args.planeID) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'No workplane selected',
        fieldId: 'planeID',
      });
    }
  }

  /**
   * Validate the selected plane exists in routine or global space.
   * Throws ActionRequiredError if invalid.
   *
   * @throws {ActionRequiredError}
   */
  private checkPlaneValid(
    args: Extract<RcStep.Arguments, { argumentKind: 'Locate' }>,
    routine: RoutineView,
    ctx: ValidationContext,
  ): void {
    const allSpace = this.getSpaceItems(routine, ctx);

    if (
      !allSpace.some(
        (item) => item.id === args.planeID && item.kind === 'plane',
      )
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Selected workplane is not valid',
        fieldId: 'planeID',
      });
    }
  }

  /**
   * Validate the position list name is set.
   * Throws ActionRequiredError if missing.
   *
   * @throws {ActionRequiredError}
   */
  private checkPositionListName(
    args: Extract<RcStep.Arguments, { argumentKind: 'Locate' }>,
  ): void {
    if (!args.positionListName) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Position list name is not configured',
        fieldId: 'positionListName',
      });
    }
  }

  /**
   * Validate the optional captureConfig when present.
   * Checks that storage equipment exists and is an FTP Server or S3 Bucket,
   * and that the save filepath is valid.
   *
   * @throws {ActionRequiredError}
   */
  private checkCaptureConfig(
    args: Extract<RcStep.Arguments, { argumentKind: 'Locate' }>,
    ctx: ValidationContext,
  ): void {
    const { captureConfig } = args;

    if (!captureConfig) {
      return;
    }

    if (!captureConfig.saveEquipmentId) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Save equipment ID is required',
        fieldId: 'captureConfig.saveEquipmentId',
      });
    }

    const storageEquipment = this.findEquipment(
      ctx,
      captureConfig.saveEquipmentId,
    );

    if (!storageEquipment) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Storage equipment not found',
        fieldId: 'captureConfig.saveEquipmentId',
      });
    }

    if (
      storageEquipment.config.kind !== 'FTPServer' &&
      storageEquipment.config.kind !== 'S3Bucket'
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Storage equipment must be an FTP Server or S3 Bucket',
        fieldId: 'captureConfig.saveEquipmentId',
      });
    }

    if (
      captureConfig.saveFilepath &&
      captureConfig.saveFilepath.kind === 'constant' &&
      captureConfig.saveFilepath.value === ''
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Save file path is required',
        fieldId: 'captureConfig.saveFilepath',
      });
    }

    if (
      captureConfig.saveFilepath &&
      ((captureConfig.saveFilepath.kind === 'constant' &&
        !captureConfig.saveFilepath.value.toString().endsWith('.png')) ||
        (captureConfig.saveFilepath.kind === 'JavaScript' &&
          !captureConfig.saveFilepath.expression.endsWith('.png"')) ||
        (captureConfig.saveFilepath.kind === 'Python' &&
          !captureConfig.saveFilepath.expression.endsWith('.png"')))
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Save file path must end with .png',
        fieldId: 'captureConfig.saveFilepath',
      });
    }

    validateCodeExpression(
      captureConfig.saveFilepath,
      'captureConfig.saveFilepath',
      'Save filepath',
    );
  }
}

export const LocateValidation: ValidationRule =
  new LocateRule().toValidationRule();
